// The receiver journey's words and masks: statuses, end reasons and insurance types in pt-BR,
// documents always masked on screen, and the form's own reading of the purpose rules the receiver
// enforces (Res. CNSP 415, art. 15; no links nor markup).
import { Match, Option, Schema } from 'effect';
import { Cnpj, Cpf, maskCnpj, maskCpf } from '@finnest/core/effect';
import { OPIN_RECEIVER_PURPOSE_MAX } from '@finnest/power-admin-contracts/opin-receiver';
import type { PowerRefusal } from '@finnest/power-admin-contracts/failure';
import type { OpinReceiverConnectionErrorCode } from '@finnest/power-admin-contracts/opin-receiver/connection-error';
import type {
  OpinReceiverConnectionStatus,
  OpinReceiverCreateConflict,
  OpinReceiverEndReason,
  OpinReceiverInsuranceType,
  OpinReceiverRevocationReason,
  OpinReceiverReconciliationCode,
  OpinReceiverUnavailableCode,
} from '@finnest/power-admin-contracts/opin-receiver';
import type {
  OpinReceiverResourceStatus,
  OpinReceiverResourceType,
} from '@finnest/power-admin-contracts/opin-receiver/shared-resources';
import type {
  ReceiverCallFailure,
  ReceiverCallRefused,
  ReceiverPendingRegistration,
} from '../client/calls.ts';

export const STATUS_LABELS: Readonly<Record<OpinReceiverConnectionStatus, string>> = {
  pending: 'Pendente',
  awaiting_auth: 'Aguardando autorização',
  syncing: 'Sincronizando',
  active: 'Ativa',
  error: 'Erro',
  revoked: 'Revogada',
  revocation_pending: 'Revogação pendente',
  expired: 'Expirada',
};

export const END_REASON_LABELS: Readonly<Record<OpinReceiverEndReason, string>> = {
  max_date_reached: 'Prazo do consentimento atingido',
  authorization_timeout: 'Autorização não concluída a tempo',
  authorization_failed: 'Falha ao iniciar a autorização',
  customer_revoked: 'Revogado na receptora',
  customer_revoked_at_transmitter: 'Revogado pelo titular na transmissora',
  transmitter_security: 'Revogado pela transmissora por segurança',
  participant_security: 'Revogado pela participante por segurança ou fraude',
};

// What is wrong with the transmitter's TLS server certificate, as the create's 503 and the
// connection's error both say it.
const CERTIFICATE_TEXTS = {
  transmitter_certificate_not_server:
    'o certificado TLS do servidor da transmissora não está marcado para uso de servidor (falta serverAuth no extendedKeyUsage); a Finnest pode incluir o endereço na exceção da implantação',
  transmitter_certificate_untrusted:
    'o certificado TLS do servidor da transmissora não é emitido por uma AC confiável',
  transmitter_certificate_wrong_host:
    'o certificado TLS do servidor da transmissora é de outro endereço',
} as const;

/** Why a connection is in error, in the operator's words, by its stable code. */
export const CONNECTION_ERROR_TEXTS: Readonly<Record<OpinReceiverConnectionErrorCode, string>> = {
  authorization_initiation_failed: 'a autorização não pôde ser iniciada na transmissora',
  authorization_code_exchange_failed: 'a transmissora não trocou o código de autorização',
  authorization_refused: 'a autorização foi recusada na transmissora',
  consent_anomaly: 'a verificação do consentimento achou um estado que pede análise',
  sync_failed: 'a sincronização falhou',
  sync_stalled: 'a sincronização não terminou a tempo',
  ...CERTIFICATE_TEXTS,
  unclassified: 'erro registrado por uma versão anterior',
};

/** How the revoke dialog names each reason the operator may choose. */
export const REVOCATION_REASON_LABELS: Readonly<Record<OpinReceiverRevocationReason, string>> = {
  holder_request: 'A pedido do titular',
  security: 'Por segurança ou fraude',
};

export const TYPE_LABELS: Readonly<Record<OpinReceiverInsuranceType, string>> = {
  patrimonial: 'Patrimonial',
  auto: 'Automóvel',
  person: 'Pessoas',
  housing: 'Habitacional',
  life_pension: 'Vida e previdência',
  pension_plan: 'Plano de previdência',
  financial_risk: 'Riscos financeiros',
  responsibility: 'Responsabilidade',
  rural: 'Rural',
  transport: 'Transportes',
  acceptance_and_branches_abroad: 'Aceitação e sucursal no exterior',
  capitalization_title: 'Título de capitalização',
  financial_assistance: 'Assistência financeira',
  customers: 'Dados cadastrais',
};

/** A CPF's length, in digits. */
export const CPF_LENGTH = 11;
const CNPJ_LENGTH = 14;

// Only the characters a document carries: digits, and capitals in an alphanumeric CNPJ.
const documentCharacters = (typed: string): string =>
  typed.toUpperCase().replaceAll(/[^\dA-Z]/gu, '');

/** The holder a lookup or a consent is for, as the screen names it: masked documents. */
export const holderText = (cpf: string, cnpj: string): string =>
  cnpj === '' ? `titular ${maskCpf(cpf)}` : `titular ${maskCpf(cpf)} · empresa ${maskCnpj(cnpj)}`;

// How a typed document is punctuated (ASCII patterns): each slot takes the next character, a mark
// sits between.
const SLOT = '#';
const CPF_PATTERN = '###.###.###-##';
const CNPJ_PATTERN = '##.###.###/####-##';

// A mark goes only before a typed character, so erasing the last one never stops at a mark.
const punctuate = (characters: string, pattern: string): string =>
  pattern.split('').reduce<{ readonly used: number; readonly text: string }>(
    ({ used, text }, mark) => {
      if (used === characters.length) return { used, text };
      if (mark === SLOT) return { used: used + 1, text: `${text}${characters.charAt(used)}` };
      return { used, text: `${text}${mark}` };
    },
    { used: 0, text: '' },
  ).text;

/** A CPF as the operator types it, punctuated; extra characters are dropped. */
export const punctuateCpf = (cpf: string): string => punctuate(cpf, CPF_PATTERN);

/** A CNPJ as the operator types it, punctuated; extra characters are dropped. */
export const punctuateCnpj = (cnpj: string): string => punctuate(cnpj, CNPJ_PATTERN);

/** A typed CPF's digits, at most a CPF's length. */
export const typedCpf = (typed: string): string =>
  documentCharacters(typed).replaceAll(/\D/gu, '').slice(0, CPF_LENGTH);

/** A typed CNPJ's characters, at most a CNPJ's length. */
export const typedCnpj = (typed: string): string => documentCharacters(typed).slice(0, CNPJ_LENGTH);

/** The purpose rules the form shows. */
export const PURPOSE_RULES = {
  length: `De 1 a ${String(OPIN_RECEIVER_PURPOSE_MAX)} caracteres, sem espaços no início ou no fim.`,
  link: 'Sem links (nada como "https://" ou "www.").',
  markup: 'Sem marcação: os caracteres < > [ ] * _ ~ ` não são aceitos.',
} as const;

const MARKUP = /[<>[\]*_~`]/u;
const LINK = /:\/\/|www\./iu;

// Each match is one code point, as the receiver counts the purpose.
const codePoints = (text: string): number => [...text.matchAll(/[\s\S]/gu)].length;

/** The purpose rules `purpose` breaks; none when the receiver would take it. */
export const brokenPurposeRules = (purpose: string): readonly string[] => [
  ...(codePoints(purpose) === 0 ||
  codePoints(purpose) > OPIN_RECEIVER_PURPOSE_MAX ||
  purpose.trim() !== purpose
    ? [PURPOSE_RULES.length]
    : []),
  ...(LINK.test(purpose) ? [PURPOSE_RULES.link] : []),
  ...(MARKUP.test(purpose) ? [PURPOSE_RULES.markup] : []),
];

const SAO_PAULO = new Intl.DateTimeFormat('pt-BR', {
  dateStyle: 'short',
  timeStyle: 'short',
  timeZone: 'America/Sao_Paulo',
});

const NONE = '—';

/** A number of months as the operator reads it: "1 mês", "12 meses". */
export const formatMonths = (months: number): string =>
  months === 1 ? '1 mês' : `${String(months)} meses`;

/** A date-time as the operator reads it, in America/Sao_Paulo. */
export const formatInstant = (instant: string | null): string => {
  if (instant === null) return NONE;
  const parsed = Date.parse(instant);
  // A stored record's text that only looks like an instant reads as it came.
  return Number.isFinite(parsed) ? SAO_PAULO.format(parsed) : instant;
};

const HTTP = {
  badRequest: 400,
  unauthorized: 401,
  forbidden: 403,
  notFound: 404,
  conflict: 409,
  unprocessable: 422,
  unavailable: 503,
} as const;

/** Why the receiver is unavailable, in the operator's words, by the code of Power's 503. */
const UNAVAILABLE_TEXTS: Readonly<Record<OpinReceiverUnavailableCode, string>> = {
  receiver_unconfigured: 'a jornada receptora não está configurada neste ambiente',
  token_unavailable: 'o servidor de autorização não emitiu o token da receptora',
  receiver_upstream_error: 'o serviço da receptora falhou',
  audit_unavailable: 'a auditoria não registrou a ação: confira a conexão antes de repetir',
  ...CERTIFICATE_TEXTS,
};

// A 503 says why, when Power names it.
const unavailableText = ({ unavailable }: ReceiverCallRefused): string =>
  unavailable === null ? 'receptora indisponível no momento' : UNAVAILABLE_TEXTS[unavailable];

/** Why a pending DCR registration attempt blocks a create, in the operator's words. */
const PENDING_REGISTRATION_TEXTS: Readonly<Record<OpinReceiverReconciliationCode, string>> = {
  dcr_attempt_before_trail:
    'uma tentativa de DCR anterior com a transmissora está pendente (anterior ao registro das chamadas)',
  dcr_attempt_unanswered:
    'uma tentativa de DCR anterior com a transmissora foi enviada e ficou sem resposta',
  dcr_attempt_answered:
    'uma tentativa de DCR anterior com a transmissora foi respondida e não concluída',
};

// The reason, then that it expires on its own, and what to run to settle it earlier once the
// transmitter confirms what it holds: the receiver's detail names the attempt, when it expires and
// the exact command.
const pendingRegistrationText = ({ code, detail }: ReceiverPendingRegistration): string =>
  `${PENDING_REGISTRATION_TEXTS[code]}; ela expira sozinha e libera uma nova tentativa, ou reconcilie antes, após confirmar com a transmissora: ${detail}`;

/** Why a create conflicts, in the operator's words. */
const CREATE_CONFLICT_TEXTS: Readonly<Record<OpinReceiverCreateConflict, string>> = {
  'create-in-progress': 'a criação anterior ainda está em andamento: tente de novo em instantes',
  'already-connected':
    'o titular já tem uma conexão ativa ou pendente com esta transmissora: acompanhe-a na lista de conexões ou revogue-a antes de pedir uma nova',
};

const refusalText = (refused: ReceiverCallRefused): string => {
  const { status, field, createConflict, pendingRegistration } = refused;
  if (createConflict !== null) return CREATE_CONFLICT_TEXTS[createConflict];
  switch (status) {
    case HTTP.unauthorized:
      return 'sua sessão terminou (inatividade ou saída): entre novamente';
    case HTTP.forbidden:
      return 'seu papel não permite esta ação';
    case HTTP.notFound:
      return 'não encontrado';
    case HTTP.conflict:
      return pendingRegistration === null
        ? 'a conexão não permite esta ação no estado atual'
        : pendingRegistrationText(pendingRegistration);
    case HTTP.badRequest:
    case HTTP.unprocessable:
      return `dados inválidos (${field})`;
    case HTTP.unavailable:
      return unavailableText(refused);
    default:
      return `erro ${String(status)}`;
  }
};

/** Why a call failed, in the operator's words. */
export const failureText = (failure: ReceiverCallFailure): string =>
  Match.valueTags(failure, {
    ReceiverCallUnreachable: () => 'Power inacessível',
    ReceiverReplyInvalid: () => 'resposta fora do contrato',
    ReceiverCallRefused: refusalText,
  });

/** The error code and trace id to quote to support, when Power refused the call naming a code. */
export const failureRefusal = (failure: ReceiverCallFailure): Option.Option<PowerRefusal> =>
  Match.valueTags(failure, {
    ReceiverCallUnreachable: () => Option.none(),
    ReceiverReplyInvalid: () => Option.none(),
    ReceiverCallRefused: ({ refusal }) => Option.fromNullOr(refusal),
  });

/** Whether `failure` is the receiver refusing the connection's current state (409). */
export const isConnectionConflict = (failure: ReceiverCallFailure): boolean =>
  Match.valueTags(failure, {
    ReceiverCallUnreachable: () => false,
    ReceiverReplyInvalid: () => false,
    ReceiverCallRefused: ({ status, createConflict }) =>
      status === HTTP.conflict && createConflict === null,
  });

/** Whether `failure` says this deployment runs no receiver: its list answers 404. */
export const isReceiverDisabled = (failure: ReceiverCallFailure): boolean =>
  Match.valueTags(failure, {
    ReceiverCallUnreachable: () => false,
    ReceiverReplyInvalid: () => false,
    ReceiverCallRefused: ({ status }) => status === HTTP.notFound,
  });

/** Whether `digits` is a CPF whose check digits hold; checked before a document is sent. */
export const isCpf = Schema.is(Cpf);

/**
 * Whether `cnpj`, numeric or alphanumeric, is a CNPJ whose check digits hold; checked before a
 * document is sent.
 */
export const isCnpj = Schema.is(Cnpj);

const DAY_MS = 86_400_000;
const HOUR_MS = 3_600_000;
const MINUTE_MS = 60_000;
const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;

const SAO_PAULO_DATE = new Intl.DateTimeFormat('pt-BR', {
  dateStyle: 'short',
  timeZone: 'America/Sao_Paulo',
});
const CALENDAR_DAY = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeZone: 'UTC' });
const REAIS = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

/** How long ago `instant` was, as the operator reads it: "há 5 min", "há 3 h", "ontem". */
export const formatRelative = (instant: string | null, now: number): string => {
  if (instant === null) return NONE;
  const elapsed = Math.max(0, now - Date.parse(instant));
  const minutes = Math.round(elapsed / MINUTE_MS);
  if (minutes < 1) return 'agora';
  if (minutes < MINUTES_PER_HOUR) return `há ${String(minutes)} min`;
  const hours = Math.round(elapsed / HOUR_MS);
  if (hours < HOURS_PER_DAY) return `há ${String(hours)} h`;
  const days = Math.round(elapsed / DAY_MS);
  return days === 1 ? 'ontem' : `há ${String(days)} dias`;
};

/** The day of a date-time, in America/Sao_Paulo. */
export const formatDate = (instant: string | null): string =>
  instant === null ? NONE : SAO_PAULO_DATE.format(Date.parse(instant));

const CALENDAR = /^\d{4}-\d{2}-\d{2}$/u;

/** A calendar day (`YYYY-MM-DD`) as the operator reads it; any other text as it came. */
export const formatDay = (day: string | null): string => {
  if (day === null) return NONE;
  const parsed = CALENDAR.test(day) ? Date.parse(`${day}T00:00:00Z`) : Number.NaN;
  return Number.isFinite(parsed) ? CALENDAR_DAY.format(parsed) : day;
};

/** A decimal amount in reais (`"1234.56"`); any other text as it came. */
export const formatMoney = (amount: string | null): string => {
  if (amount === null) return NONE;
  const reais = Number(amount);
  return Number.isFinite(reais) ? REAIS.format(reais) : amount;
};

/** How many days before it lapses a consent asks to be renewed. */
export const EXPIRING_DAYS = 7;

/** Where a connection's consent stands: ended, not granted yet, or running out over its validity. */
export type ConsentWindow =
  | { readonly kind: 'ended'; readonly text: string }
  | { readonly kind: 'awaiting' }
  | {
      readonly kind: 'running';
      readonly daysLeft: number;
      /** How much of the requested validity has passed, 0 to 100. */
      readonly elapsed: number;
      readonly expiring: boolean;
    };

const AVERAGE_MONTH_DAYS = 30.44;
const PERCENT = 100;
const ENDED_STATUSES: ReadonlySet<OpinReceiverConnectionStatus> = new Set(['revoked', 'expired']);

/** A connection's consent window at `now`. */
export const consentWindowOf = (
  connection: {
    readonly status: OpinReceiverConnectionStatus;
    readonly consentExpiresAt: string | null;
    readonly endReason: OpinReceiverEndReason | null;
    readonly consentTerms: { readonly validityMonths: number };
  },
  now: number,
): ConsentWindow => {
  if (ENDED_STATUSES.has(connection.status)) {
    return {
      kind: 'ended',
      text: connection.endReason === null ? 'Encerrada' : END_REASON_LABELS[connection.endReason],
    };
  }
  if (connection.consentExpiresAt === null) return { kind: 'awaiting' };
  const left = Date.parse(connection.consentExpiresAt) - now;
  const daysLeft = Math.max(0, Math.ceil(left / DAY_MS));
  const total = connection.consentTerms.validityMonths * AVERAGE_MONTH_DAYS * DAY_MS;
  const elapsed = Math.min(PERCENT, Math.max(0, PERCENT - (left / total) * PERCENT));
  return { kind: 'running', daysLeft, elapsed, expiring: daysLeft <= EXPIRING_DAYS };
};

/** The consent window in words: "Faltam 318 dias", "Vence em 5 dias", "Aguardando concessão". */
export const consentWindowText = (window: ConsentWindow): string => {
  if (window.kind === 'ended') return window.text;
  if (window.kind === 'awaiting') return 'Aguardando concessão';
  const days = window.daysLeft === 1 ? '1 dia' : `${String(window.daysLeft)} dias`;
  return window.expiring ? `Vence em ${days}` : `Faltam ${days}`;
};

/** How the detail names each kind of resource a consent shares. */
export const RESOURCE_TYPE_LABELS: Readonly<Record<OpinReceiverResourceType, string>> = {
  CUSTOMERS_PERSONAL_IDENTIFICATIONS: 'Identificação (pessoa física)',
  CUSTOMERS_PERSONAL_QUALIFICATION: 'Qualificação (pessoa física)',
  CUSTOMERS_PERSONAL_ADDITIONALINFO: 'Informações complementares (pessoa física)',
  CUSTOMERS_BUSINESS_IDENTIFICATIONS: 'Identificação (pessoa jurídica)',
  CUSTOMERS_BUSINESS_QUALIFICATION: 'Qualificação (pessoa jurídica)',
  CUSTOMERS_BUSINESS_ADDITIONALINFO: 'Informações complementares (pessoa jurídica)',
  CAPITALIZATION_TITLES: 'Título de capitalização',
  PENSION_PLAN: 'Plano de previdência',
  LIFE_PENSION: 'Vida e previdência',
  FINANCIAL_ASSISTANCE: 'Assistência financeira',
  DAMAGES_AND_PEOPLE_PATRIMONIAL: 'Patrimonial',
  DAMAGES_AND_PEOPLE_RESPONSIBILITY: 'Responsabilidade',
  DAMAGES_AND_PEOPLE_TRANSPORT: 'Transportes',
  DAMAGES_AND_PEOPLE_FINANCIAL_RISKS: 'Riscos financeiros',
  DAMAGES_AND_PEOPLE_RURAL: 'Rural',
  DAMAGES_AND_PEOPLE_AUTO: 'Automóvel',
  DAMAGES_AND_PEOPLE_HOUSING: 'Habitacional',
  DAMAGES_AND_PEOPLE_PERSON: 'Pessoas',
  DAMAGES_AND_PEOPLE_ACCEPTANCE_AND_BRANCHES_ABROAD: 'Aceitação e sucursal no exterior',
};

/** How the detail names a shared resource's status. */
export const RESOURCE_STATUS_LABELS: Readonly<Record<OpinReceiverResourceStatus, string>> = {
  AVAILABLE: 'Disponível',
  UNAVAILABLE: 'Indisponível',
  TEMPORARILY_UNAVAILABLE: 'Temporariamente indisponível',
  PENDING_AUTHORISATION: 'Aguardando autorização',
};

// Words a transmitter's name shares with most others, skipped by its initials.
const COMMON_WORDS = new Set([
  's.a.',
  'sa',
  'seguros',
  'seguro',
  'seguradora',
  'gerais',
  'de',
  'e',
  'cia',
]);
const INITIALS = 2;

/** A transmitter's initials, from the words of its name that tell it apart. */
export const initialsOf = (name: string): string => {
  const words = name.split(/\s+/u).filter((word) => word !== '');
  const telling = words.filter((word) => !COMMON_WORDS.has(word.toLowerCase()));
  return (telling.length === 0 ? words : telling)
    .slice(0, INITIALS)
    .map((word) => word.charAt(0).toUpperCase())
    .join('');
};

/** The avatar colours, all with white text at a readable contrast. */
const AVATAR_COLORS = [
  '#5b32e6',
  '#0f6f74',
  '#c2410c',
  '#4322c3',
  '#b42318',
  '#2563eb',
  '#7c3aed',
  '#0e7490',
  '#9a3412',
  '#15803d',
] as const;

// A small polynomial string hash: its seed, its multiplier and the prime it stays under.
const HASH_SEED = 7;
const HASH_MULTIPLIER = 31;
const HASH_MODULUS = 1_000_003;

/** One stable colour per key (a transmitter's id), so a transmitter always looks the same. */
export const avatarColorOf = (key: string): string => {
  const hash = [...Array(key.length).keys()].reduce(
    (sum, index) => (sum * HASH_MULTIPLIER + key.charCodeAt(index)) % HASH_MODULUS,
    HASH_SEED,
  );
  return AVATAR_COLORS[hash % AVATAR_COLORS.length] ?? AVATAR_COLORS[0];
};
